import { createFileRoute, Link } from "@tanstack/react-router";
import { BarChart3, BookOpen, Eye, Images, Paintbrush, Settings, Sparkles, Trophy } from "lucide-react";
import { PageLayout } from "../../components/PageLayout";
import { Button } from "../../components/ui/button";
import { Logo } from "../../components/ui/Logo";
import { QuotaEducationCard } from "../../components/ui/quota-education-card";
import { useAuth } from "../../contexts/AuthContext";
import { useFirstTimeEducation } from "../../hooks/useFirstTimeEducation";

export const Route = createFileRoute("/_layout/")({
    head: () => ({
        meta: [{ name: "title", content: "StoryDoodle - Interactive AI-Powered Storytelling for Kids" }],
    }),
    component: Index,
});

function Index() {
    const { user, loading } = useAuth();

    const { shouldShowEducation, markEducationSeen, storiesLimit } = useFirstTimeEducation();

    if (loading) {
        return (
            <div className="min-h-screen page-gradient flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary/30 border-t-primary mx-auto mb-4"></div>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <PageLayout title="StoryDoodle">
            {/* Hero Section */}
            <div className="container mx-auto px-4 sm:px-6 py-6">
                <div className="text-center space-y-6">
                    <div className="space-y-4">
                        <div className="flex flex-col items-center space-y-2">
                            <Logo size="xxl" />
                        </div>
                        <h2 className="text-5xl font-bold leading-tight">
                            <span className="text-primary">Story.</span> <span className="text-secondary">Doodle.</span>{" "}
                            <span className="text-accent">Adventure.</span>
                        </h2>
                        <h2 className="text-5xl font-bold leading-tight">
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))]">
                                With AI Magic
                            </span>
                        </h2>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            Embark on epic interactive tales where your drawings shape the narrative and bring stories
                            to life.
                        </p>
                        <p className="text-sm text-muted-foreground max-w-xl mx-auto">
                            Perfect for families! Parents can create accounts and enjoy creative storytelling together
                            with children of all ages.
                        </p>
                    </div>

                    {/* Action Buttons - Only for non-authenticated users */}
                    {!loading && !user && (
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <Button size="lg" asChild className="text-lg px-8 py-6">
                                <Link to="/sign-up">Get Started Free</Link>
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                asChild
                                className="text-lg px-8 py-6 hover:text-primary hover:border-primary"
                            >
                                <Link to="/sign-in">I Have an Account</Link>
                            </Button>
                        </div>
                    )}

                    {/* Loading placeholder for auth buttons */}
                    {loading && (
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <div className="h-14 w-40 bg-muted animate-pulse rounded-lg"></div>
                            <div className="h-14 w-44 bg-muted animate-pulse rounded-lg"></div>
                        </div>
                    )}
                </div>

                {user && (
                    <div className="mt-12">
                        <div className="max-w-2xl mx-auto space-y-4">
                            {/* First-Time User Education - above Create Your Story */}
                            {shouldShowEducation && (
                                <QuotaEducationCard
                                    isVisible={shouldShowEducation}
                                    onDismiss={markEducationSeen}
                                    storiesLimit={storiesLimit}
                                />
                            )}

                            <Link
                                to="/story-mode"
                                className="group block p-8 rounded-2xl border-2 border-primary/20 hover:border-primary/40 bg-gradient-to-br from-primary/5 to-secondary/5 hover:from-primary/10 hover:to-secondary/10 transition-all hover:shadow-xl"
                            >
                                <div className="text-center space-y-4">
                                    <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto group-hover:bg-primary/20 transition-colors">
                                        <Sparkles className="h-10 w-10 text-primary" />
                                    </div>
                                    <h3 className="text-3xl font-bold">Create Your Story</h3>
                                    <p className="text-lg text-muted-foreground">
                                        Create fun stories where your drawings shape the tale and bring it to life
                                    </p>

                                    <div className="pt-4">
                                        <span className="text-primary font-semibold text-xl group-hover:underline">
                                            Start Now →
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>
                )}

                <div className="mt-12">
                    <h3 className="text-3xl font-bold text-center mb-8">How It Works</h3>
                    <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                        <div className="text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-secondary/10 to-secondary/20 rounded-2xl flex items-center justify-center mx-auto">
                                <Paintbrush className="h-8 w-8 text-secondary" />
                            </div>
                            <div className="space-y-3">
                                <h4 className="text-xl font-bold text-secondary">Draw Your Ideas</h4>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Draw anything you can imagine - heroes, spaceships, forests, or magical places
                                </p>
                            </div>
                        </div>

                        <div className="text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-primary/10 to-primary/20 rounded-2xl flex items-center justify-center mx-auto">
                                <Eye className="h-8 w-8 text-primary" />
                            </div>
                            <div className="space-y-3">
                                <h4 className="text-xl font-bold text-primary">AI Sees Your Drawing</h4>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    The computer looks at your drawing and figures out what happens next in the story
                                </p>
                            </div>
                        </div>

                        <div className="text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-accent/10 to-accent/20 rounded-2xl flex items-center justify-center mx-auto">
                                <BookOpen className="h-8 w-8 text-accent" />
                            </div>
                            <div className="space-y-3">
                                <h4 className="text-xl font-bold text-accent">Your Story Grows</h4>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Each drawing you make helps build an amazing story just for you
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Coming Soon Section */}
                <div className="mt-16">
                    <div className="text-center mb-8">
                        <h3 className="text-3xl font-bold mb-3">Coming Soon</h3>
                        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                            Exciting new features to enhance your creative journey
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
                        <div className="text-center space-y-4 p-6 rounded-xl border border-muted bg-card/50">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500/10 to-blue-600/20 rounded-xl flex items-center justify-center mx-auto">
                                <BarChart3 className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="space-y-2">
                                <h4 className="font-semibold">Story History & Progress</h4>
                                <p className="text-muted-foreground text-sm">
                                    Track your creative journey with story stats and drawing history
                                </p>
                            </div>
                        </div>

                        <div className="text-center space-y-4 p-6 rounded-xl border border-muted bg-card/50">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500/10 to-purple-600/20 rounded-xl flex items-center justify-center mx-auto">
                                <Images className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="space-y-2">
                                <h4 className="font-semibold">Drawing Gallery</h4>
                                <p className="text-muted-foreground text-sm">
                                    Browse and revisit all your amazing artwork
                                </p>
                            </div>
                        </div>

                        <div className="text-center space-y-4 p-6 rounded-xl border border-muted bg-card/50">
                            <div className="w-12 h-12 bg-gradient-to-br from-yellow-500/10 to-yellow-600/20 rounded-xl flex items-center justify-center mx-auto">
                                <Trophy className="h-6 w-6 text-yellow-600" />
                            </div>
                            <div className="space-y-2">
                                <h4 className="font-semibold">Achievement System</h4>
                                <p className="text-muted-foreground text-sm">
                                    Earn badges for completed stories and creative milestones
                                </p>
                            </div>
                        </div>

                        <div className="text-center space-y-4 p-6 rounded-xl border border-muted bg-card/50">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-500/10 to-green-600/20 rounded-xl flex items-center justify-center mx-auto">
                                <Settings className="h-6 w-6 text-green-600" />
                            </div>
                            <div className="space-y-2">
                                <h4 className="font-semibold">Enhanced Story Controls</h4>
                                <p className="text-muted-foreground text-sm">
                                    More ways to customize your storytelling experience
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </PageLayout>
    );
}
